/**
 * Blob E2E non ancora decifrabili — NON scartati.
 * Persistiti in IndexedDB per retry dopo auto-resync chiavi.
 */
import { openDB, type DBSchema, type IDBPDatabase } from 'idb'

export interface PendingDecryptBlob {
  /** Fingerprint stabile del ciphertext */
  id: string
  blob: string
  contactId?: string
  peerSession?: string
  createdAt: number
  attempts: number
  lastAttemptAt: number
  /** id messaggio placeholder in chat (se creato) */
  placeholderMsgId?: string
}

interface PendingDb extends DBSchema {
  pending: {
    key: string
    value: PendingDecryptBlob
    indexes: { 'by-contact': string }
  }
}

let dbPromise: Promise<IDBPDatabase<PendingDb>> | null = null

function getDb(): Promise<IDBPDatabase<PendingDb>> {
  if (!dbPromise) {
    dbPromise = openDB<PendingDb>('zion-pending-decrypt', 1, {
      upgrade(db) {
        const store = db.createObjectStore('pending', { keyPath: 'id' })
        store.createIndex('by-contact', 'contactId')
      },
    })
  }
  return dbPromise
}

export async function fingerprintBlob(blob: string): Promise<string> {
  const sample = `${blob.length}:${blob.slice(0, 1500)}:${blob.slice(-200)}`
  const digest = await crypto.subtle.digest({ name: 'SHA-256' }, new TextEncoder().encode(sample))
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
    .slice(0, 40)
}

export async function savePendingDecrypt(
  entry: Omit<PendingDecryptBlob, 'id' | 'attempts' | 'lastAttemptAt' | 'createdAt'> & {
    id?: string
    attempts?: number
  },
): Promise<PendingDecryptBlob> {
  const id = entry.id || (await fingerprintBlob(entry.blob))
  const db = await getDb()
  const existing = await db.get('pending', id)
  const row: PendingDecryptBlob = {
    id,
    blob: entry.blob,
    contactId: entry.contactId || existing?.contactId,
    peerSession: entry.peerSession || existing?.peerSession,
    createdAt: existing?.createdAt ?? Date.now(),
    attempts: entry.attempts ?? existing?.attempts ?? 0,
    lastAttemptAt: Date.now(),
    placeholderMsgId: entry.placeholderMsgId ?? existing?.placeholderMsgId,
  }
  await db.put('pending', row)
  return row
}

export async function getPendingDecrypt(id: string): Promise<PendingDecryptBlob | undefined> {
  const db = await getDb()
  return db.get('pending', id)
}

export async function listPendingDecryptForContact(contactId: string): Promise<PendingDecryptBlob[]> {
  const db = await getDb()
  const cid = contactId.trim()
  if (!cid) return []
  return db.getAllFromIndex('pending', 'by-contact', cid)
}

export async function listAllPendingDecrypt(): Promise<PendingDecryptBlob[]> {
  const db = await getDb()
  return db.getAll('pending')
}

export async function removePendingDecrypt(id: string): Promise<void> {
  const db = await getDb()
  await db.delete('pending', id)
}

export async function bumpPendingAttempt(id: string): Promise<void> {
  const db = await getDb()
  const row = await db.get('pending', id)
  if (!row) return
  row.attempts += 1
  row.lastAttemptAt = Date.now()
  await db.put('pending', row)
}

/** GC: scarta pending più vecchi di 7 giorni. */
export async function gcPendingDecrypt(maxAgeMs = 7 * 24 * 60 * 60 * 1000): Promise<void> {
  const db = await getDb()
  const all = await db.getAll('pending')
  const cutoff = Date.now() - maxAgeMs
  for (const row of all) {
    if (row.createdAt < cutoff || row.attempts > 40) {
      await db.delete('pending', row.id)
    }
  }
}
